'use client'

import { Cell, DoubleImage, Image } from '@vinted/web-ui'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { ConditionalWrapper } from '@marketplace-web/shared/conditional-wrapper'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/domain/assets'
import { formatCurrencyAmount } from '@marketplace-web/domain/currency-data'
import { HelpCenterTransactionModel } from 'types/models/help-center'

const FALLBACK_IMAGE = 'image.svg'

type Props = {
  transaction: HelpCenterTransactionModel
  cellOptions?: ComponentProps<typeof Cell>
}

const HelpCenterTransactionItem = ({ transaction, cellOptions }: Props) => {
  const translate = useTranslate('user')
  const asset = useAsset('assets/icons')
  const noPhotoAsset = useAsset('assets/no-photo')
  const { locale } = useIntl()

  const {
    price,
    statusTitle,
    itemTitle,
    itemPhoto,
    itemPath,
    bundleCount,
    oppositeUserUsername,
    oppositeUserPhoto,
    oppositeUserPath,
  } = transaction

  const getOppositeUserUsername = () => {
    if (!oppositeUserUsername) return translate('deleted_user')

    return oppositeUserUsername
  }

  const renderImageWrapper = (path?: string) => (children: JSX.Element) => (
    <a href={path} data-testid="hc-home-transaction-item-link">
      {children}
    </a>
  )

  const renderTransactionPrefix = () => {
    const itemPathUrl = itemPath || undefined
    const primaryImage = (
      <ConditionalWrapper
        condition={!cellOptions?.url && !!itemPathUrl}
        wrapper={renderImageWrapper(itemPathUrl)}
      >
        <Image
          src={itemPhoto || asset(FALLBACK_IMAGE)}
          alt={itemTitle}
          label={bundleCount?.toString()}
          size={Image.Size.Large}
          styling={Image.Styling.Rounded}
        />
      </ConditionalWrapper>
    )

    const oppositeUserPathUrl = oppositeUserPath || undefined
    const secondaryImage = (
      <ConditionalWrapper
        condition={!cellOptions?.url && !!oppositeUserPathUrl}
        wrapper={renderImageWrapper(oppositeUserPathUrl)}
      >
        <Image
          src={oppositeUserPhoto || noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
          alt={getOppositeUserUsername()}
          size={Image.Size.Regular}
          styling={Image.Styling.Circle}
        />
      </ConditionalWrapper>
    )

    return <DoubleImage primary={primaryImage} secondary={secondaryImage} />
  }

  const renderTransactionBody = () => {
    let formattedAmount: string | undefined

    if (price) {
      formattedAmount = formatCurrencyAmount(price, locale)
    }

    const transactionBody = [getOppositeUserUsername(), formattedAmount, statusTitle]
      .filter(Boolean)
      .join(' • ')

    return transactionBody
  }

  return (
    <Cell
      prefix={renderTransactionPrefix()}
      title={itemTitle}
      body={renderTransactionBody()}
      {...cellOptions}
    />
  )
}

export default HelpCenterTransactionItem
